import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { updateSessionProgressFromResumeData, calculateResumeProgress } from '../utils/progressSync';
import { checkEnglishVersionUpdateNeeded } from '../utils/englishVersion';

export const useResumeData = (id, user, currentSession, extractedResumeData, showError = null, showSuccess = null) => {
  const [resumeData, setResumeData] = useState({
    name: '',
    name_en: '', // 영어 이름 추가
    email: '',
    phone: '',
    address: '',
    github: '',
    title: '',
    summary: '',
    keyStrengths: [],
    experiences: [],
    education: [],
    skills: [],
    certificates: [],
    papers: [],
    additionalInfo: [],
    social_links: [],
    english_version: null,
  });
  
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [existingPortfolio, setExistingPortfolio] = useState(null);
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  const [englishUpdateNeeded, setEnglishUpdateNeeded] = useState(false);
  const [englishUpdateInfo, setEnglishUpdateInfo] = useState(null);
  const [lastSyncedData, setLastSyncedData] = useState(null); // 마지막 동기화된 데이터 추적
  const [modifiedFields, setModifiedFields] = useState(new Set()); // 수정된 필드 추적

  // 최신 경력에서 타이틀 자동 생성
  const updateTitleFromLatestExperience = (experiences) => {
    if (!experiences || experiences.length === 0) return '';
    
    const currentJobs = experiences.filter(exp => 
      exp.endDate && (exp.endDate.trim() === '현재' || exp.endDate.trim().toLowerCase() === 'current')
    );
    
    if (currentJobs.length > 0) {
      const sortedCurrentJobs = currentJobs.sort((a, b) => {
        if (!a.startDate) return 1;
        if (!b.startDate) return -1;
        return b.startDate.localeCompare(a.startDate);
      });
      
      const latestCurrentJob = sortedCurrentJobs[0];
      if (latestCurrentJob?.position?.trim()) {
        return latestCurrentJob.position.trim();
      }
    }
    
    const pastJobs = experiences.filter(exp => 
      !exp.endDate || (exp.endDate.trim() !== '현재' && exp.endDate.trim().toLowerCase() !== 'current')
    );
    
    if (pastJobs.length > 0) {
      const sortedPastJobs = pastJobs.sort((a, b) => {
        if (!a.startDate) return 1;
        if (!b.startDate) return -1;
        return b.startDate.localeCompare(a.startDate);
      });
      
      const latestPastJob = sortedPastJobs[0];
      if (latestPastJob?.position?.trim()) {
        return latestPastJob.position.trim();
      }
    }
    
    return '';
  };

  // 기존 포트폴리오 확인 - 포트폴리오 존재 여부를 먼저 확인
  const checkExistingPortfolio = async (userId, sessionId) => {
    try {
      setPortfolioLoading(true);
      
      if (!userId || !sessionId) {
        setExistingPortfolio(null);
        return;
      }

      // 1단계: 해당 세션에 포트폴리오가 존재하는지 먼저 확인 (count 사용)
      const { count, error: countError } = await supabase
        .from('portfolios')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('session_id', sessionId)
        .is('deleted_at', null);  // 삭제되지 않은 포트폴리오만 카운트

      if (countError) {
        console.warn('Portfolio count 확인 오류:', countError.code, countError.message);
        setExistingPortfolio(null);
        return;
      }

      // 2단계: 포트폴리오가 존재하지 않으면 조회하지 않음
      if (count === 0) {
        setExistingPortfolio(null);
        return;
      }

      // 포트폴리오가 존재하면 실제 데이터 조회
      const { data: portfolio, error } = await supabase
        .from('portfolios')
        .select('*')
        .eq('user_id', userId)
        .eq('session_id', sessionId)
        .is('deleted_at', null)  // 삭제되지 않은 포트폴리오만 조회
        .single();

      if (error && error.code !== 'PGRST116') {
        console.warn('Portfolio 조회 오류:', error.code, error.message);
        setExistingPortfolio(null);
        return;
      }

      setExistingPortfolio(portfolio);

    } catch (error) {
      console.warn('Portfolio 처리 중 오류 (무시됨):', error.message);
      setExistingPortfolio(null);
    } finally {
      setPortfolioLoading(false);
    }
  };

  // 동기화가 필요한 필드 정의 (이메일, 전화번호, 소셜링크 제외)
  const SYNC_REQUIRED_FIELDS = [
    'name', 'title', 'summary', 'keyStrengths', 
    'experiences', 'education', 'skills', 
    'certificates', 'papers'
  ];

  // 필드 값이 실제로 변경되었는지 확인
  const hasFieldChanged = (field, oldValue, newValue) => {
    // 배열인 경우
    if (Array.isArray(oldValue) && Array.isArray(newValue)) {
      return JSON.stringify(oldValue) !== JSON.stringify(newValue);
    }
    // 객체인 경우
    if (typeof oldValue === 'object' && typeof newValue === 'object') {
      return JSON.stringify(oldValue) !== JSON.stringify(newValue);
    }
    // 기본 값 비교
    return oldValue !== newValue;
  };

  // 영어 버전 업데이트 필요 여부 확인
  const checkEnglishUpdateStatus = async (sessionId) => {
    try {
      const updateInfo = await checkEnglishVersionUpdateNeeded(sessionId);
      
      // 영어 버전이 있고, 동기화가 필요한 필드가 수정된 경우에만 업데이트 필요
      if (updateInfo.needsUpdate && resumeData.english_version && modifiedFields.size > 0) {
        // 동기화가 필요한 필드 중 하나라도 수정되었는지 확인
        const syncNeeded = Array.from(modifiedFields).some(field => 
          SYNC_REQUIRED_FIELDS.includes(field)
        );
        setEnglishUpdateNeeded(syncNeeded);
      } else {
        setEnglishUpdateNeeded(false);
      }
      
      setEnglishUpdateInfo(updateInfo);
      
    } catch (error) {
      console.warn('영어 버전 업데이트 체크 중 오류:', error);
      setEnglishUpdateNeeded(false);
      setEnglishUpdateInfo(null);
    }
  };

  // 현재 세션 데이터 로드
  const loadCurrentSessionData = async () => {
    try {
      setLoading(true);
      setSessionData(currentSession);

      // 항상 DB에서 최신 데이터를 로드
      const { data: resumes, error: resumeError } = await supabase
        .from('resumes')
        .select('*')
        .eq('session_id', currentSession.id)
        .eq('is_active', true)
        .limit(1);

      if (resumeError) {
        console.error('❌ Resume data query error:', resumeError);
      }

      if (resumes && resumes.length > 0) {
        const resume = resumes[0];
        const autoTitle = resume.title || updateTitleFromLatestExperience(resume.experiences);

        const loadedData = {
          name: resume.name || '',
          name_en: resume.name_en || '', // 영어 이름 로드
          email: resume.email || '',
          phone: resume.phone || '',
          address: resume.address || '',
          github: resume.github || '',
          title: autoTitle,
          summary: resume.summary || '',
          keyStrengths: resume.key_strengths || [],
          experiences: resume.experiences || [],
          education: resume.education || [],
          skills: resume.skills || [],
          certificates: resume.certificates || [],
          papers: resume.papers || [],
          additionalInfo: [],
          social_links: resume.social_links || [],
          english_version: resume.english_version || null,
        };
        setResumeData(loadedData);
        setLastSyncedData(loadedData); // 초기 데이터를 동기화 기준점으로 설정
        setModifiedFields(new Set()); // 수정 필드 초기화
      } else if (extractedResumeData && extractedResumeData.name) {
        // DB에 데이터가 없는 경우에만 extractedResumeData 사용
        setResumeData(extractedResumeData);
      } else {
        setResumeData(prev => ({
          ...prev,
          name: currentSession.name || ''
        }));
      }

      await checkExistingPortfolio(user.id, currentSession.id);

      // 영어 버전 업데이트 상태 확인
      await checkEnglishUpdateStatus(currentSession.id);
    } catch (error) {
      console.error('❌ Error loading current session data:', error);
      alert('An error occurred while loading data.');
    } finally {
      setLoading(false);
    }
  };

  // 특정 세션 데이터 로드
  const loadResumeData = async () => {
    try {
      setLoading(true);

      const { data: session, error: sessionError } = await supabase
        .from('consulting_sessions')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .is('deleted_at', null)  // 삭제되지 않은 세션만 조회
        .single();

      if (sessionError) {
      console.error('❌ 세션 조회 오류:', sessionError);
      if (showError) showError('세션을 찾을 수 없습니다.');
      return;
      }

      setSessionData(session);

      const { data: resumes, error: resumeError } = await supabase
        .from('resumes')
        .select('*')
        .eq('session_id', id)
        .eq('is_active', true)
        .limit(1);

      if (resumeError) {
        console.error('❌ 이력서 데이터 조회 오류:', resumeError);
      }

      if (resumes && resumes.length > 0) {
        const resume = resumes[0];
        const autoTitle = resume.title || updateTitleFromLatestExperience(resume.experiences);
        
        const loadedData = {
          name: resume.name || '',
          name_en: resume.name_en || '', // 영어 이름 로드
          email: resume.email || '',
          phone: resume.phone || '',
          address: resume.address || '',
          github: resume.github || '',
          title: autoTitle,
          summary: resume.summary || '',
          keyStrengths: resume.key_strengths || [],
          experiences: resume.experiences || [],
          education: resume.education || [],
          skills: resume.skills || [],
          certificates: resume.certificates || [],
          papers: resume.papers || [],
          additionalInfo: [],
          social_links: resume.social_links || [],
          english_version: resume.english_version || null,
        };
        setResumeData(loadedData);
        setLastSyncedData(loadedData); // 초기 데이터를 동기화 기준점으로 설정
        setModifiedFields(new Set()); // 수정 필드 초기화
      } else {
        setResumeData(prev => ({
          ...prev,
          name: session.name || ''
        }));
      }
      
      await updateSessionProgressFromResumeData(id, user.id);
      await checkExistingPortfolio(user.id, id);
      
      // 영어 버전 업데이트 상태 확인
      await checkEnglishUpdateStatus(id);
    } catch (error) {
      console.error('❌ 데이터 로드 중 오류:', error);
      if (showError) showError('데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 이력서 저장
  const saveResumeData = async () => {
    const sessionId = id || currentSession?.id;
    if (!sessionId || !user) {
      if (showError) showError('저장할 세션 정보가 없습니다.');
      return;
    }

    try {
      const dbUpdates = {
        name: resumeData.name || '',
        name_en: resumeData.name_en || '', // 영어 이름 저장
        email: resumeData.email || '',
        phone: resumeData.phone || '',
        address: resumeData.address || '',
        github: resumeData.github || '',
        title: resumeData.title || '',
        summary: resumeData.summary || '',
        key_strengths: Array.isArray(resumeData.keyStrengths) ? resumeData.keyStrengths.filter(s => s && s.trim()) : [],
        experiences: Array.isArray(resumeData.experiences) ? resumeData.experiences : [],
        education: Array.isArray(resumeData.education) ? resumeData.education : [],
        skills: Array.isArray(resumeData.skills) ? resumeData.skills.filter(s => s && s.trim()) : [],
        certificates: Array.isArray(resumeData.certificates) ? resumeData.certificates : [],
        papers: Array.isArray(resumeData.papers) ? resumeData.papers : [],
        social_links: Array.isArray(resumeData.social_links) ? resumeData.social_links : [],
      };

      const { data: existingRecord, error: checkError } = await supabase
        .from('resumes')
        .select('id')
        .eq('session_id', sessionId)
        .eq('is_active', true)
        .limit(1);
        
      if (checkError) {
        console.error('🔍 기존 레코드 확인 오류:', checkError);
      }
      
      let result;
      if (existingRecord && existingRecord.length > 0) {
        result = await supabase
          .from('resumes')
          .update(dbUpdates)
          .eq('session_id', sessionId)
          .eq('is_active', true);
      } else {
        result = await supabase
          .from('resumes')
          .insert({
            ...dbUpdates,
            session_id: sessionId,
            user_id: user.id,
            is_active: true
          });
      }
      
      const { error } = result;

      if (error) {
        console.error('❌ 저장 오류:', error);
        if (showError) showError('저장 중 오류가 발생했습니다: ' + (error.message || JSON.stringify(error)));
      } else {
        // 성공 메시지는 호출하는 쪽에서 처리
        // 저장 후 진행률 업데이트
        await updateSessionProgressFromResumeData(sessionId, user.id);
        
        // 저장 성공 시 수정 필드 초기화하지 않음 (영어 동기화를 위해 유지)
        // 영어 버전 동기화 후에만 초기화됨
        
        // 저장 후 영어 버전 업데이트 상태 체크
        await checkEnglishUpdateStatus(sessionId);
      }
    } catch (error) {
      console.error('❌ 저장 중 예외:', error);
      if (showError) showError('저장 중 오류가 발생했습니다.');
    }
  };

  // 이력서 완성도 체크
  const isResumeComplete = () => {
    const requiredFields = {
      name: resumeData.name?.trim(),
      email: resumeData.email?.trim(),
      phone: resumeData.phone?.trim(),
      summary: resumeData.summary?.trim(),
      keyStrengths: resumeData.keyStrengths?.length > 0 && resumeData.keyStrengths.some(s => s?.trim()),
      experiences: resumeData.experiences?.length > 0,
      skills: resumeData.skills?.length > 0 && resumeData.skills.some(s => s?.trim())
    };
    
    return Object.values(requiredFields).every(field => field);
  };

  // 이력서 데이터 업데이트
  const updateResumeData = async (fieldOrData, value) => {
    let updatedData;
    const newModifiedFields = new Set(modifiedFields);
    
    // 객체가 전달된 경우 (전체 데이터 업데이트)
    if (typeof fieldOrData === 'object' && fieldOrData !== null) {
      updatedData = fieldOrData;
      
      // 모든 필드 검사하여 변경된 필드 추적
      if (lastSyncedData) {
        Object.keys(updatedData).forEach(field => {
          if (SYNC_REQUIRED_FIELDS.includes(field) && 
              hasFieldChanged(field, lastSyncedData[field], updatedData[field])) {
            newModifiedFields.add(field);
          }
        });
      }
    } else {
      // 필드별 업데이트
      updatedData = {
        ...resumeData,
        [fieldOrData]: value
      };
      
      // 변경된 필드 추적 (동기화가 필요한 필드만)
      if (lastSyncedData && SYNC_REQUIRED_FIELDS.includes(fieldOrData) && 
          hasFieldChanged(fieldOrData, lastSyncedData[fieldOrData], value)) {
        newModifiedFields.add(fieldOrData);
      }
    }
    
    setResumeData(updatedData);
    setModifiedFields(newModifiedFields);
    
    // 실시간 진행률 업데이트 (메모리 상태 기반)
    const sessionId = id || currentSession?.id;
    
    if (sessionId && user?.id) {
      // 500ms 후에 진행률 업데이트 (너무 자주 호출되지 않도록)
      clearTimeout(updateResumeData.timeoutId);
      updateResumeData.timeoutId = setTimeout(async () => {
        try {
          // 메모리에 있는 최신 데이터로 진행률 계산 (이미 import된 함수 사용)
          const currentProgress = calculateResumeProgress(updatedData);
          
          // DB의 진행률 직접 업데이트
          const { error: updateError } = await supabase
            .from('consulting_sessions')
            .update({ progress_percentage: currentProgress })
            .eq('id', sessionId)
            .eq('user_id', user.id);

          if (updateError) {
            console.error('❌ 세션 진행률 업데이트 실패:', updateError);
          }
        } catch (error) {
          console.error('❌ 진행률 업데이트 중 오류:', error);
        }
      }, 1000); // 1초로 증가
    } else {
      console.warn('⚠️ 세션 ID 또는 사용자 ID가 없어서 진행률 업데이트를 건너뜁니다.');
    }
  };

  // 데이터 로드 effect
  useEffect(() => {
    if (user) {
      if (id) {
        loadResumeData();
      } else if (currentSession) {
        loadCurrentSessionData();
      } else {
        setLoading(false);
      }
    }
  }, [id, user, currentSession]);

  return {
    resumeData,
    sessionData,
    loading,
    existingPortfolio,
    portfolioLoading,
    updateResumeData,
    saveResumeData,
    isResumeComplete,
    setExistingPortfolio,
    updateTitleFromLatestExperience,
    refreshResumeData: id ? loadResumeData : loadCurrentSessionData,
    englishUpdateNeeded,
    englishUpdateInfo,
    checkEnglishUpdateStatus,
    modifiedFields,
    setModifiedFields,
    setLastSyncedData
  };
};